import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { inventoryApi, InventoryPart, InventoryTransaction } from '../services/api';
import { renderError } from '../utils/errorUtils';
import {
  formatLocaleDecimal,
  isLocaleDecimalTyping,
  parseLocaleNumber,
} from '../utils/money';

interface InventoryManagementProps {
  part: InventoryPart;
  onStockUpdated: (updatedPart: InventoryPart) => void;
}

const InventoryManagement: React.FC<InventoryManagementProps> = ({ part, onStockUpdated }) => {
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState<'adjust' | 'income' | null>(null);
  const [confirmTyped, setConfirmTyped] = useState('');
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Adjust = rewrite absolute on-hand qty by default (not the same as income/add)
  const [adjustForm, setAdjustForm] = useState({
    adjustment_type: 'set' as 'add' | 'remove' | 'set',
    quantity: Number(part.stock_quantity) || 0,
    reason: '',
    notes: ''
  });

  // Income = receive / purchase (adds to stock)
  const [incomeForm, setIncomeForm] = useState({
    quantity: 0,
    supplier: '',
    cost_per_unit: Number(part.unit_cost) || 0,
    purchase_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  // Keep a free-text draft so users can type "0,2" (EU decimal comma).
  const [costPerUnitText, setCostPerUnitText] = useState(
    formatLocaleDecimal(part.unit_cost)
  );

  const currentStock = Number(part.stock_quantity) || 0;

  const projectedAdjustStock = (() => {
    if (adjustForm.adjustment_type === 'set') return adjustForm.quantity;
    if (adjustForm.adjustment_type === 'add') return currentStock + (adjustForm.quantity || 0);
    return Math.max(0, currentStock - (adjustForm.quantity || 0));
  })();

  const projectedIncomeStock = currentStock + (incomeForm.quantity || 0);

  const adjustWouldChange =
    adjustForm.adjustment_type === 'set'
      ? projectedAdjustStock !== currentStock
      : (adjustForm.quantity || 0) > 0;

  const adjustReady =
    adjustWouldChange &&
    String(adjustForm.reason || '').trim().length >= 3 &&
    Number.isFinite(adjustForm.quantity) &&
    (adjustForm.adjustment_type === 'set' ? adjustForm.quantity >= 0 : adjustForm.quantity > 0);

  const incomeReady = (incomeForm.quantity || 0) > 0;

  const openAdjustDialog = () => {
    setAdjustForm({
      adjustment_type: 'set',
      quantity: currentStock,
      reason: '',
      notes: '',
    });
    setAdjustDialogOpen(true);
  };

  const openIncomeDialog = () => {
    const unitCost = Number(part.unit_cost) || 0;
    setIncomeForm({
      quantity: 0,
      supplier: part.supplier || '',
      cost_per_unit: unitCost,
      purchase_date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setCostPerUnitText(formatLocaleDecimal(unitCost));
    setIncomeDialogOpen(true);
  };

  const isProtectedPart =
    part.part_number === 'SYS-COMM' ||
    Number(part.part_id) === 60 ||
    /system\s*commission/i.test(String(part.part_name || ''));

  const confirmToken = String(part.part_number || '').trim();
  const confirmTypedOk =
    !isProtectedPart ||
    (confirmToken.length > 0 && confirmTyped.trim() === confirmToken);

  const requestAdjustConfirm = () => {
    if (!adjustReady) {
      setError(
        !adjustWouldChange
          ? 'Quantity is unchanged — nothing to apply.'
          : 'Enter a short reason (at least 3 characters) before changing stock.'
      );
      return;
    }
    setConfirmTyped('');
    setConfirmKind('adjust');
    setConfirmOpen(true);
  };

  const requestIncomeConfirm = () => {
    if (!incomeReady) {
      setError('Enter a positive quantity received before recording income.');
      return;
    }
    setConfirmTyped('');
    setConfirmKind('income');
    setConfirmOpen(true);
  };

  const handleAdjustStock = async () => {
    try {
      setLoading(true);
      setError(null);
      setConfirmOpen(false);

      const response = await inventoryApi.adjustStock(part.part_id, adjustForm);

      const newStock = response.data.new_stock;
      const updatedPart = {
        ...part,
        stock_quantity: newStock,
        inventory_value: newStock * (Number(part.unit_cost) || 0),
        is_out_of_stock: newStock === 0,
        is_low_stock:
          newStock > 0 &&
          Number(part.minimum_stock_level) > 0 &&
          newStock <= Number(part.minimum_stock_level),
      };
      onStockUpdated(updatedPart);

      if (response.data.warning) {
        setSuccessMessage(`Stock updated. Warning: ${response.data.warning}`);
      } else if (adjustForm.adjustment_type === 'set') {
        setSuccessMessage(`Stock set to ${newStock} ${part.unit_of_measure || ''}`.trim());
      } else {
        setSuccessMessage(
          `Stock ${adjustForm.adjustment_type === 'add' ? 'added' : 'removed'} successfully`
        );
      }
      setAdjustDialogOpen(false);
      setConfirmKind(null);
    } catch (err: any) {
      const details = err.response?.data?.details;
      const base = err.response?.data?.error || 'Failed to adjust stock';
      setError(renderError(details ? `${base}: ${details}` : base));
    } finally {
      setLoading(false);
    }
  };

  const handleAddIncome = async () => {
    try {
      setLoading(true);
      setError(null);
      setConfirmOpen(false);

      const response = await inventoryApi.addIncome(part.part_id, incomeForm);

      const newStock = response.data.new_stock;
      const unitCost = incomeForm.cost_per_unit || part.unit_cost;
      const updatedPart = {
        ...part,
        stock_quantity: newStock,
        supplier: incomeForm.supplier || part.supplier,
        unit_cost: unitCost,
        inventory_value: newStock * (Number(unitCost) || 0),
        is_out_of_stock: newStock === 0,
        is_low_stock:
          newStock > 0 &&
          Number(part.minimum_stock_level) > 0 &&
          newStock <= Number(part.minimum_stock_level),
      };
      onStockUpdated(updatedPart);

      if (response.data.warning) {
        setSuccessMessage(`Stock income saved (qty updated). Warning: ${response.data.warning}`);
      } else {
        setSuccessMessage(
          `Income recorded: +${incomeForm.quantity} → ${newStock} ${part.unit_of_measure || ''}`.trim()
        );
      }
      setIncomeDialogOpen(false);
      setConfirmKind(null);
    } catch (err: any) {
      const details = err.response?.data?.details;
      const base = err.response?.data?.error || 'Failed to record stock income';
      setError(renderError(details ? `${base}: ${details}` : base));
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionHistory = async () => {
    try {
      const response = await inventoryApi.getHistory(part.part_id);
      setTransactions(response.data.transactions);
    } catch (err: any) {
      setError(renderError(err.response?.data?.error || 'Failed to load transaction history'));
    }
  };

  const getStockStatusChip = () => {
    if (part.is_out_of_stock) {
      return <Chip icon={<WarningIcon />} label="Out of Stock" color="error" size="small" />;
    } else if (part.is_low_stock) {
      return <Chip icon={<WarningIcon />} label="Low Stock" color="warning" size="small" />;
    } else {
      return <Chip icon={<InventoryIcon />} label="In Stock" color="success" size="small" />;
    }
  };

  const quantityLabel =
    adjustForm.adjustment_type === 'set'
      ? 'New stock quantity'
      : adjustForm.adjustment_type === 'add'
        ? 'Quantity to add'
        : 'Quantity to remove';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6">
          Current Stock: {part.stock_quantity} {part.unit_of_measure}
        </Typography>
        {getStockStatusChip()}
        <Typography variant="body2" color="text.secondary">
          Value: €{Number(part.inventory_value || 0).toFixed(2)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={openAdjustDialog}
        >
          Adjust Stock
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openIncomeDialog}
          color="success"
        >
          Add Income
        </Button>
        <Tooltip title="View Transaction History">
          <IconButton
            onClick={() => {
              setHistoryDialogOpen(true);
              loadTransactionHistory();
            }}
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Dialog open={adjustDialogOpen} onClose={() => setAdjustDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Stock</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Adjust rewrites on-hand quantity (default: set absolute stock). To record a purchase that
              adds stock with supplier/cost, use Add Income instead.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Current stock: <strong>{part.stock_quantity}</strong> {part.unit_of_measure}
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Adjustment Type</InputLabel>
              <Select
                value={adjustForm.adjustment_type}
                onChange={(e) => {
                  const next = e.target.value as 'add' | 'remove' | 'set';
                  setAdjustForm({
                    ...adjustForm,
                    adjustment_type: next,
                    quantity: next === 'set' ? Number(part.stock_quantity) || 0 : 0,
                  });
                }}
                label="Adjustment Type"
              >
                <MenuItem value="set">Set stock (rewrite quantity)</MenuItem>
                <MenuItem value="add">Add to stock</MenuItem>
                <MenuItem value="remove">Remove from stock</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={quantityLabel}
              type="number"
              value={adjustForm.quantity}
              onChange={(e) =>
                setAdjustForm({
                  ...adjustForm,
                  quantity: parseInt(e.target.value, 10) || 0,
                })
              }
              inputProps={{ min: 0 }}
              required
              helperText={
                adjustForm.adjustment_type === 'set'
                  ? `Will set stock to ${projectedAdjustStock} (replaces ${currentStock})`
                  : adjustForm.adjustment_type === 'add'
                    ? `Will become ${projectedAdjustStock}`
                    : `Will become ${projectedAdjustStock}`
              }
            />

            <TextField
              fullWidth
              label="Reason (required)"
              value={adjustForm.reason}
              onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
              placeholder="e.g., Stock count correction, Damaged items, etc."
              required
              helperText="Required to prevent accidental stock changes"
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={adjustForm.notes}
              onChange={(e) => setAdjustForm({ ...adjustForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={requestAdjustConfirm}
            variant="contained"
            color="warning"
            disabled={loading || !adjustReady}
          >
            Review change…
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={incomeDialogOpen} onClose={() => setIncomeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Stock Income</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              Income adds received stock (purchase). It does not replace the current quantity.
              Current: {part.stock_quantity} {part.unit_of_measure}
            </Alert>
            <TextField
              fullWidth
              label="Quantity Received"
              type="number"
              value={incomeForm.quantity}
              onChange={(e) =>
                setIncomeForm({ ...incomeForm, quantity: parseInt(e.target.value, 10) || 0 })
              }
              required
              helperText={
                incomeForm.quantity > 0
                  ? `After income: ${projectedIncomeStock}`
                  : undefined
              }
            />

            <TextField
              fullWidth
              label="Supplier"
              value={incomeForm.supplier}
              onChange={(e) => setIncomeForm({ ...incomeForm, supplier: e.target.value })}
              placeholder={part.supplier || 'Enter supplier name'}
            />

            <TextField
              fullWidth
              label="Cost per Unit (€)"
              type="text"
              inputMode="decimal"
              value={costPerUnitText}
              onChange={(e) => {
                const raw = e.target.value;
                if (!isLocaleDecimalTyping(raw)) return;
                setCostPerUnitText(raw);
                const parsed = parseLocaleNumber(raw, NaN);
                setIncomeForm({
                  ...incomeForm,
                  cost_per_unit: Number.isFinite(parsed) ? parsed : 0,
                });
              }}
              onBlur={() => {
                const parsed = parseLocaleNumber(costPerUnitText, 0);
                setIncomeForm({ ...incomeForm, cost_per_unit: parsed });
                setCostPerUnitText(formatLocaleDecimal(parsed));
              }}
              helperText="Use comma or dot for decimals (e.g. 0,20 or 0.20)"
              placeholder="0,00"
            />

            <TextField
              fullWidth
              label="Purchase Date"
              type="date"
              value={incomeForm.purchase_date}
              onChange={(e) => setIncomeForm({ ...incomeForm, purchase_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={incomeForm.notes}
              onChange={(e) => setIncomeForm({ ...incomeForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncomeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={requestIncomeConfirm}
            variant="contained"
            color="warning"
            disabled={loading || !incomeReady}
          >
            Review income…
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => !loading && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmKind === 'income' ? 'Confirm stock income' : 'Confirm stock change'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This updates live inventory. Double-check before confirming.
          </Alert>
          {confirmKind === 'adjust' ? (
            <Typography variant="body2" sx={{ mb: isProtectedPart ? 2 : 0 }}>
              <strong>{part.part_name || part.part_number}</strong>
              <br />
              {currentStock} → <strong>{projectedAdjustStock}</strong> {part.unit_of_measure}
              <br />
              Type: {adjustForm.adjustment_type}
              <br />
              Reason: {adjustForm.reason || '—'}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mb: isProtectedPart ? 2 : 0 }}>
              <strong>{part.part_name || part.part_number}</strong>
              <br />
              Add +{incomeForm.quantity} → <strong>{projectedIncomeStock}</strong>{' '}
              {part.unit_of_measure}
              <br />
              Supplier: {incomeForm.supplier || '—'}
            </Typography>
          )}
          {isProtectedPart && (
            <TextField
              fullWidth
              label={`Type ${confirmToken} to confirm`}
              value={confirmTyped}
              onChange={(e) => setConfirmTyped(e.target.value)}
              placeholder={confirmToken}
              helperText="Protected system part — type the part number to apply"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            Go back
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={loading || !confirmTypedOk}
            onClick={() => {
              if (confirmKind === 'income') handleAddIncome();
              else handleAdjustStock();
            }}
          >
            {loading ? 'Saving…' : 'Yes, apply change'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Transaction History - {part.part_name}</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Previous</TableCell>
                  <TableCell>New</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Cost/Unit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.transaction_id}>
                    <TableCell>{new Date(transaction.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.transaction_type}
                        color={transaction.transaction_type === 'income' ? 'success' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{transaction.quantity}</TableCell>
                    <TableCell>{transaction.previous_stock}</TableCell>
                    <TableCell>{transaction.new_stock}</TableCell>
                    <TableCell>{transaction.reason || '-'}</TableCell>
                    <TableCell>{transaction.supplier || '-'}</TableCell>
                    <TableCell>
                      {transaction.cost_per_unit != null
                        ? `€${Number(transaction.cost_per_unit).toFixed(2)}`
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryManagement;
